import { Component, computed, inject, input, output } from '@angular/core';
import type { InstallationAddress } from '@essent/installation-address';
import type { SupplyAddress } from '@essent/new-customer';
import {
  getOrderCrossSellEnabled,
  type OrderAddressFormValues,
} from '@innogy/become-a-customer/state';
import {
  CommonUiFormsModule,
  type DropdownItem,
  type RadioItem,
} from '@innogy/common-ui/forms';
import type { UppercaseConverter } from '@innogy/utils-deprecated';
import {
  normalizeDateString,
  uppercaseConverter,
  UtilsModule,
} from '@innogy/utils-deprecated';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { CommonUiProgressiveFormsModule } from '@innogy/common-ui/progressive-forms';
import { SupplyAddressComponent } from '@innogy/become-a-customer/ui';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CSMaskDirective } from '@legacy/utils-contentsquare';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';

import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';
import { OrderFulfillmentInswitchInhouseComponent } from './order-fulfillment-inswitch-inhouse/order-fulfillment-inswitch-inhouse.component';
import { ToolTipComponent } from '@legacy/common-ui-utility-components';

@Component({
  selector: 'wl-order-fulfillment-address[progressiveFormState]',
  templateUrl: './order-fulfillment-address.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    CommonModule,
    CommonUiProgressiveFormsModule,
    SupplyAddressComponent,
    CommonUiFormsModule,
    AutomationIdDirective,
    CSMaskDirective,
    OrderFulfillmentInswitchInhouseComponent,
    UtilsModule,
    JssModule,
    ToolTipComponent,
  ],
})
export class OrderFulfillmentAddressComponent extends OrderFulfillmentStepComponent<OrderAddressFormValues> {
  _uppercaseConverter: UppercaseConverter = uppercaseConverter;

  private readonly store$ = inject(Store);

  readonly labelsShownAboveFields = input(false);
  readonly residenceOrWorkplaceOptions = input.required<RadioItem[]>();
  readonly exclusivelyCorporateUsageOptions = input.required<RadioItem[]>();
  readonly correspondenceHouseNumberExtensions =
    input.required<DropdownItem[]>();
  readonly supplyAddress = input<Partial<SupplyAddress>>();
  readonly correspondenceAddress = input<InstallationAddress>();

  readonly changeSupplyAddress = output();
  readonly inswitchOpenLegalModal = output();

  readonly crossSellFeatureEnabled = this.store$.selectSignal(
    getOrderCrossSellEnabled
  );

  override readonly progressiveFormCardContent = computed(() => ({
    title: this.orderContent().addressCardTitle.value ?? '',
    subtitle: this.orderContent().addressCardSubtitle,
    submitButtonText: this.crossSellFeatureEnabled()
      ? this.orderContent().addressCardSubmitButtonWithCrossSell
      : this.orderContent().addressCardSubmitButton,
    stepNumber: this.stepNumber(),
  }));

  readonly controls = computed(() => this.formState().controls);
  readonly selectedInswitchOrInhouseOptionLabel = computed(
    () => this.formState().value.inswitchInhouse?.value?.label
  );
  readonly selectedResidenceOrWorkplaceOption = computed(() =>
    this.residenceOrWorkplaceOptions().find(
      (option) => option.value === this.controls().residenceOrWorkplace?.value
    )
  );
  readonly selectedStartDate = computed(() =>
    normalizeDateString(this.controls().selectedStartDate.value)
  );
  readonly hideCorrespondenceHouseNumberExtensions = computed(
    () => !this.controls().houseNumberCorrespondence?.value
  );

  changeSupplyAddressClick = (e: Event) => {
    e.preventDefault();
    this.changeSupplyAddress.emit();
  };

  inswitchOpenLegalModalClick = () => {
    this.inswitchOpenLegalModal.emit();
  };
}
